import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { findNikkeInCatalog } from '../../nikke-catalog/domain/nikkeCatalog.js';
import { NikkeIcon } from '../../nikke-icons/shared/NikkeIcon.jsx';
import {
  findPersonalNikke,
  formatFavoriteItemStage,
  formatOverloadValue,
  formatPersonalNikkeNumber,
  summarizeOverloadOptions,
} from '../../personal-nikke/domain/personalNikke.js';
import { loadUnionMemberDetail } from '../../union-roster/api/unionMemberDetailApi.js';
import { useSharedNikkeCatalog, useSharedPersonalNikkeLookup } from './RaidPracticeDeckPicker.jsx';
import { RaidPracticeNikkeCard } from './RaidPracticeNikkeCard.jsx';

const OVERLOAD_SHORT_LABELS = {
  IncElementDmg: '우코',
  StatAtk: '공증',
  StatAmmoLoad: '장탄',
  StatCritical: '크확',
  StatCriticalDamage: '크뎀',
  StatDef: '방어',
  StatChargeDamage: '차뎀',
  StatChargeTime: '차속',
  StatHit: '명중',
  StatAccuracyCircle: '명중',
};

function text(value) {
  return String(value ?? '').trim();
}

function SquadMemberProgression({ personal }) {
  const core = Number(personal?.core) || 0;
  const grade = Math.min(3, Math.max(0, core > 0 ? 3 : Number(personal?.grade) || 0));
  return (
    <span className="raid-practice-squad-member-progression" aria-label={`돌파 ${grade}/3${core > 0 ? `, 코강 ${core}` : ''}`}>
      <span className="raid-practice-squad-member-stars" aria-hidden="true">
        {Array.from({ length: 3 }, (_, index) => <i className={index < grade ? 'active' : ''} key={index}>★</i>)}
      </span>
      {core > 0 ? <b>{core >= 7 ? 'MAX' : core}</b> : null}
    </span>
  );
}

function OverloadChip({ effect }) {
  return (
    <span className="raid-practice-squad-overload-chip" title={`${effect.label} ${formatOverloadValue(effect)}`}>
      <b>{OVERLOAD_SHORT_LABELS[effect.effectType] || effect.label}</b>
      <em>{formatOverloadValue(effect)}</em>
    </span>
  );
}

function OverloadChipRow({ effects }) {
  const rowRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(effects.length);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return undefined;

    const update = () => {
      const available = row.clientWidth;
      const gap = 6;
      let used = 0;
      let count = 0;
      for (const child of measure.children) {
        const next = used + (count ? gap : 0) + child.getBoundingClientRect().width;
        if (next > available) break;
        used = next;
        count += 1;
      }
      setVisibleCount(count);
    };

    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(row);
    return () => observer.disconnect();
  }, [effects]);

  if (!effects.length) return null;
  return (
    <div className="raid-practice-squad-overload" ref={rowRef} aria-label="오버로드 옵션 합계">
      <span className="raid-practice-squad-overload-measure" ref={measureRef} aria-hidden="true">
        {effects.map((effect) => <OverloadChip key={`measure-${effect.effectType}-${effect.valueType}`} effect={effect} />)}
      </span>
      {effects.slice(0, visibleCount).map((effect) => (
        <OverloadChip key={`${effect.effectType}-${effect.valueType}`} effect={effect} />
      ))}
    </div>
  );
}

function SquadMemberDetail({ item, code, personal, personalEnabled }) {
  const effects = useMemo(
    () => summarizeOverloadOptions(personal?.equipment || []),
    [personal],
  );
  const displayName = item?.name || code || '니케 정보 없음';

  return (
    <article className="raid-practice-squad-member">
      <div className="raid-practice-squad-member-art">
        <RaidPracticeNikkeCard
          item={item}
          code={code}
          showPersonalOverload={false}
        />
      </div>
      <div className="raid-practice-squad-member-info">
        <div className="raid-practice-squad-member-heading">
          <strong className="raid-practice-squad-member-name" title={displayName}>{displayName}</strong>
          {personal ? <SquadMemberProgression personal={personal} /> : null}
        </div>
        {personal ? (
          <>
            <div className="raid-practice-squad-member-stats">
              <span title="전투력"><NikkeIcon type="stat" iconKey="combat" label="전투력" fallbackName="combat" /><b>{formatPersonalNikkeNumber(personal.combat)}</b></span>
            </div>
            <div className="raid-practice-squad-member-build" aria-label="스킬과 소장품·애장품">
              {personal.skills.map((level, index) => <span key={index} title={`${index + 1}번 스킬 레벨`}>{level}</span>)}
              <em>{formatFavoriteItemStage(personal.favoriteItemTid, personal.favoriteItemLevel)}</em>
            </div>
            <OverloadChipRow effects={effects} />
          </>
        ) : (
          <small>{personalEnabled ? '동기화된 개인 니케 정보 없음' : '개인 상세 정보 비공개'}</small>
        )}
      </div>
    </article>
  );
}

export function RaidPracticeSquadDetailModal({ deck = [], personalEnabled = true, memberOpenid = '', onClose }) {
  const { catalog, loading, error } = useSharedNikkeCatalog();
  const normalizedMemberOpenid = text(memberOpenid);
  const ownPersonalLookup = useSharedPersonalNikkeLookup(personalEnabled && !normalizedMemberOpenid);
  const [memberPersonalLookup, setMemberPersonalLookup] = useState(null);
  const [memberLoading, setMemberLoading] = useState(Boolean(personalEnabled && normalizedMemberOpenid));
  const [memberError, setMemberError] = useState('');
  const codes = useMemo(() => (Array.isArray(deck) ? deck : []).map(text).filter(Boolean).slice(0, 5), [deck]);

  useEffect(() => {
    let active = true;
    if (!personalEnabled || !normalizedMemberOpenid) {
      setMemberPersonalLookup(null);
      setMemberLoading(false);
      setMemberError('');
      return () => { active = false; };
    }

    setMemberLoading(true);
    setMemberError('');
    loadUnionMemberDetail(normalizedMemberOpenid)
      .then((detail) => {
        if (!active) return;
        setMemberPersonalLookup(detail?.lookup || null);
        if (detail?.personalError) setMemberError(detail.personalError);
      })
      .catch((reason) => {
        if (active) setMemberError(reason instanceof Error ? reason.message : '멤버 니케 정보를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (active) setMemberLoading(false);
      });
    return () => { active = false; };
  }, [normalizedMemberOpenid, personalEnabled]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => { if (event.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const personalLookup = normalizedMemberOpenid ? memberPersonalLookup : ownPersonalLookup;
  const detailLoading = loading || memberLoading;
  const detailError = error || memberError;

  const modal = (
    <div className="raid-practice-squad-detail-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}>
      <section className="raid-practice-squad-detail-modal" role="dialog" aria-modal="true" aria-labelledby="raid-practice-squad-detail-title">
        <header>
          <div><strong id="raid-practice-squad-detail-title">스쿼드</strong><span>{codes.length}/5</span></div>
          <button type="button" onClick={onClose} aria-label="상세정보 닫기">×</button>
        </header>
        <div className="raid-practice-squad-detail-list">
          {detailLoading ? <p className="raid-practice-squad-detail-state" role="status" aria-busy="true"><span aria-hidden="true" />니케 정보를 불러오는 중…</p> : null}
          {!detailLoading && detailError ? <p className="raid-practice-squad-detail-state error">{detailError}</p> : null}
          {!detailLoading && !codes.length ? <p className="raid-practice-squad-detail-state">저장된 덱 정보가 없습니다.</p> : null}
          {!detailLoading && !detailError ? codes.map((code, index) => {
            const item = findNikkeInCatalog(catalog, code);
            return (
              <SquadMemberDetail
                key={`${code}-${index}`}
                item={item}
                code={code}
                personal={personalEnabled ? findPersonalNikke(personalLookup, item) : null}
                personalEnabled={personalEnabled}
              />
            );
          }) : null}
        </div>
      </section>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modal, document.body) : null;
}
